import React, { useState, useMemo, useEffect } from 'react';
import {
  SparklesIcon,
  UserIcon,
  UsersIcon,
  ChevronDownIcon,
  XMarkIcon,
  TrophyIcon,
  HandThumbUpIcon,
  FireIcon,
  HeartIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

// --- MOCK DATA ---

// Reusing MOCK_EMPLOYEES for recipients
const MOCK_EMPLOYEES = [
    { id: 'E1001', name: 'Arjun Singh', department: 'Tech' },
    { id: 'E1002', name: 'Priya Sharma', department: 'HR' },
    { id: 'E1003', name: 'Vikram Kumar', department: 'Sales' },
    { id: 'E1004', name: 'Ravi Menon', department: 'Finance' },
    { id: 'E1005', name: 'Sonia Das', department: 'Marketing' },
    { id: 'E1006', name: 'Alok Gupta', department: 'Creative' },
    { id: 'E1007', name: 'Team Alpha', department: 'Group' }, // Mock team/group
    { id: 'E1008', name: 'Finance Team', department: 'Group' },
];

const APPRECIATION_CATEGORIES = [
  { name: 'Star Performer', icon: '🌟', color: 'bg-yellow-100 text-yellow-800' },
  { name: 'Teamwork Champion', icon: '🤝', color: 'bg-indigo-100 text-indigo-800' },
  { name: 'Innovation Award', icon: '💡', color: 'bg-blue-100 text-blue-800' },
  { name: 'Going Extra Mile', icon: '🔥', color: 'bg-red-100 text-red-800' },
  { name: 'Customer First', icon: '❤️', color: 'bg-pink-100 text-pink-800' },
];

const INITIAL_POSTS = [
  {
    id: 1,
    type: 'individual',
    recipients: [{ id: 'E1001', name: 'Arjun Singh' }],
    category: 'Innovation Award',
    message: 'Fantastic work solving the complex integration bug! Great innovative thinking.',
    date: '2025-11-28',
    sender: 'Admin',
    badge: '💡',
  },
  {
    id: 2,
    type: 'team',
    recipients: [{ id: 'E1007', name: 'Team Alpha' }],
    category: 'Teamwork Champion',
    message: 'Big shoutout to Team Alpha for collaborating seamlessly on the recent launch.',
    date: '2025-11-25',
    sender: 'Priya Sharma (HR)',
    badge: '🤝',
  },
];

const getBadgeDetails = (categoryName) => {
    return APPRECIATION_CATEGORIES.find(c => c.name === categoryName) || APPRECIATION_CATEGORIES[0];
};

const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

// --- SUB-COMPONENTS ---

/**
 * Single Appreciation Post Card
 */
const AppreciationCard = ({ post }) => {
  const { icon, color } = getBadgeDetails(post.category);
  
  const recipientNames = post.recipients.map(r => r.name).join(', ');
  const isTeam = post.type === 'team';

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-indigo-500 hover:shadow-xl transition duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-full ${color} flex items-center`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <p className="text-xs font-roboto text-gray-500">{formatDate(post.date)}</p>
      </div>

      <h3 className="text-xl font-poppins font-bold text-gray-900 mb-2">
        {post.category}
      </h3>
      
      <div className="flex items-center text-md font-semibold text-indigo-600 mb-3">
        {isTeam ? (
          <UsersIcon className="h-5 w-5 mr-2" />
        ) : (
          <UserIcon className="h-5 w-5 mr-2" />
        )}
        To: {recipientNames}
      </div>

      <p className="text-gray-700 font-roboto text-base italic border-l-4 border-gray-200 pl-3 py-1">
        "{post.message}"
      </p>

      <div className="mt-4 pt-3 border-t text-sm text-gray-500 font-medium flex justify-end">
        From: {post.sender}
      </div>
    </div>
  );
};

/**
 * New Appreciation Modal Form
 */
const NewAppreciationModal = ({ isOpen, onClose, onPost }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    type: 'individual', // 'individual' or 'team'
    recipients: [],
    category: APPRECIATION_CATEGORIES[0].name,
    message: '',
  });
  
  // Use a unique key list for the recipient picker to exclude already selected members
  const recipientIds = formData.recipients.map(r => r.id);
  const availableRecipients = useMemo(() => {
    return MOCK_EMPLOYEES.filter(emp => !recipientIds.includes(emp.id));
  }, [recipientIds]);
  
  // Filter available recipients based on type
  const isTeamMode = formData.type === 'team';
  const filteredRecipients = availableRecipients.filter(emp => 
    isTeamMode ? emp.department === 'Group' : emp.department !== 'Group'
  );


  if (!isOpen) return null;

  const handleRecipientAdd = (id, name) => {
    setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, { id, name }],
    }));
  };

  const handleRecipientRemove = (id) => {
    setFormData(prev => ({
        ...prev,
        recipients: prev.recipients.filter(r => r.id !== id),
    }));
  };

  const handlePost = () => {
    if (formData.recipients.length === 0 || !formData.message.trim()) {
        console.error("Please select a recipient and write a message.");
        return;
    }
    
    const newPost = {
      id: Date.now(),
      type: formData.type,
      recipients: formData.recipients,
      category: formData.category,
      message: formData.message.trim(),
      date: new Date().toISOString().split('T')[0],
      sender: 'Admin (You)', // Assuming the user is the Admin
      badge: getBadgeDetails(formData.category).icon,
    };

    onPost(newPost);
    onClose();
    // Reset internal state
    setStep(1);
    setFormData({
        type: 'individual',
        recipients: [],
        category: APPRECIATION_CATEGORIES[0].name,
        message: '',
    });
  };
  
  // Validation for moving to next step
  const isStep1Valid = formData.recipients.length > 0 && formData.category;
  
  // Find current category details for preview
  const currentCategory = getBadgeDetails(formData.category);

  // --- Modal Content Rendering ---
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h4 className="text-xl font-poppins font-semibold text-gray-800">1. Select Recipient & Category</h4>

            {/* Recipient Type Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setFormData(p => ({ ...p, type: 'individual', recipients: [] }))}
                className={`flex-1 flex items-center justify-center p-3 rounded-lg font-semibold transition ${
                  formData.type === 'individual' ? 'bg-white shadow-md text-indigo-600' : 'text-gray-600 hover:text-indigo-600'
                }`}
              >
                <UserIcon className="h-5 w-5 mr-2" /> Individual Employee
              </button>
              <button
                onClick={() => setFormData(p => ({ ...p, type: 'team', recipients: [] }))}
                className={`flex-1 flex items-center justify-center p-3 rounded-lg font-semibold transition ${
                  formData.type === 'team' ? 'bg-white shadow-md text-indigo-600' : 'text-gray-600 hover:text-indigo-600'
                }`}
              >
                <UsersIcon className="h-5 w-5 mr-2" /> Team / Group
              </button>
            </div>

            {/* Recipient Selector */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                    {isTeamMode ? 'Select Team/Group' : 'Select Employee'}*
                </label>
                
                {/* Selected Recipients */}
                <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-gray-300 rounded-lg bg-white">
                    {formData.recipients.map(r => (
                        <span key={r.id} className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full">
                            {r.name}
                            <button 
                                onClick={() => handleRecipientRemove(r.id)}
                                className="ml-2 text-indigo-600 hover:text-indigo-900 transition"
                            >
                                <XMarkIcon className="h-4 w-4"/>
                            </button>
                        </span>
                    ))}
                    {formData.recipients.length === 0 && <span className="text-gray-400 text-sm italic">No recipients selected.</span>}
                </div>

                {/* Add Recipient Dropdown */}
                <select
                    onChange={(e) => {
                        const [id, name] = e.target.value.split('|');
                        handleRecipientAdd(id, name);
                    }}
                    value="" // Must reset value after selection to allow selecting the same option again
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-white text-sm"
                    disabled={isTeamMode && formData.recipients.length > 0} // Only one team selection allowed
                >
                    <option value="" disabled>Search or Select Recipient...</option>
                    {filteredRecipients.map(emp => (
                        <option key={emp.id} value={`${emp.id}|${emp.name}`}>
                            {emp.id} - {emp.name} ({emp.department})
                        </option>
                    ))}
                </select>
                {isTeamMode && formData.recipients.length > 0 && 
                 <p className="text-xs text-red-500">Only one team/group can be selected per post.</p>
                }
            </div>

            {/* Category Selector */}
            <div className="space-y-3 pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700">
                    Select Appreciation Category*
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {APPRECIATION_CATEGORIES.map(category => (
                        <button
                            key={category.name}
                            onClick={() => setFormData(p => ({ ...p, category: category.name }))}
                            className={`p-3 rounded-xl border-2 transition duration-150 text-center shadow-sm hover:shadow-md ${
                                formData.category === category.name 
                                    ? 'border-indigo-600 bg-indigo-50 text-indigo-800 font-bold' 
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <span className="text-2xl block mb-1">{category.icon}</span>
                            <span className="text-xs font-poppins">{category.name}</span>
                        </button>
                    ))}
                </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h4 className="text-xl font-poppins font-semibold text-gray-800">2. Craft Your Shoutout Message</h4>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Appreciation Message*
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                rows="5"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 font-roboto resize-none"
                placeholder={`Write a personalized message for ${formData.recipients.map(r => r.name).join(', ')}...`}
                maxLength={300}
              />
              <p className="text-xs text-right text-gray-500 mt-1">{formData.message.length}/300 characters</p>
            </div>

            {/* Preview Card */}
            <div className="mt-6 p-4 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50">
                <h5 className="text-md font-poppins font-semibold text-indigo-700 mb-3">Preview:</h5>
                <div className="flex items-start">
                    <span className={`text-3xl p-3 rounded-full mr-4 ${currentCategory.color}`}>{currentCategory.icon}</span>
                    <div>
                        <p className="font-poppins font-bold text-lg text-gray-900">{currentCategory.name}</p>
                        <p className="text-sm font-roboto text-gray-600">To: {formData.recipients.map(r => r.name).join(', ')}</p>
                        <p className="text-sm font-roboto italic mt-2 text-gray-700">
                            "{formData.message || "Your message will appear here..."}"
                        </p>
                    </div>
                </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    // Modal Overlay (High z-index to ensure visibility)
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-[1000] flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[75vh] overflow-y-auto transform transition-all duration-300">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between items-center">
          <h3 className="text-2xl font-poppins font-bold text-gray-900 flex items-center">
            <TrophyIcon className="h-6 w-6 mr-2 text-indigo-600"/>
            New Recognition Shoutout
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 font-roboto">
          {renderStepContent()}
        </div>

        {/* Modal Footer (Navigation) */}
        <div className="p-4 bg-gray-100 flex justify-between rounded-b-xl sticky bottom-0 z-10">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-200 transition disabled:opacity-50"
          >
            <ChevronDownIcon className="h-4 w-4 rotate-90 inline-block mr-1"/> Back
          </button>
          
          <div className="space-x-3">
            {step < 2 && (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={!isStep1Valid}
                className="px-6 py-2 text-sm font-semibold rounded-lg shadow-md bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50"
              >
                Next Step <ChevronDownIcon className="h-4 w-4 -rotate-90 inline-block ml-1"/>
              </button>
            )}

            {step === 2 && (
              <button
                onClick={handlePost}
                disabled={!formData.message.trim()}
                className="px-6 py-2 text-sm font-semibold rounded-lg shadow-xl bg-teal-600 text-white hover:bg-teal-700 transition disabled:opacity-50 flex items-center"
              >
                <StarIcon className="h-5 w-5 mr-2"/> Post Shoutout
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// --- MAIN APP COMPONENT ---

export default function EmployeeAppreciation() {
  const [posts, setPosts] = useState(INITIAL_POSTS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState('All'); // 'All', 'individual', 'team'
  const [searchTerm, setSearchTerm] = useState('');

  const handlePost = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  const filteredPosts = useMemo(() => {
    let results = posts;

    // 1. Filter by Type
    if (filterType !== 'All') {
      results = results.filter(p => p.type === filterType);
    }

    // 2. Filter by Search Term (Recipient or Message/Category)
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      results = results.filter(p => 
        p.message.toLowerCase().includes(lowerSearch) ||
        p.category.toLowerCase().includes(lowerSearch) ||
        p.recipients.some(r => r.name.toLowerCase().includes(lowerSearch))
      );
    }

    // Sort by Date (newest first)
    return results.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [posts, filterType, searchTerm]);

  // Metric calculation
  const totalShoutouts = posts.length;
  const individualShoutouts = posts.filter(p => p.type === 'individual').length;
  const teamShoutouts = posts.filter(p => p.type === 'team').length;

  return (
    <div 
      className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      <NewAppreciationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onPost={handlePost} 
      />

      {/* Page Header and New Post Button */}
      <div className="mb-8 border-b border-gray-200 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 font-poppins flex items-center">
            <SparklesIcon className="h-8 w-8 mr-3 text-pink-600"/>
            Employee Recognition Feed
          </h1>
          <p className="text-gray-500 mt-1 text-md font-roboto">
            Celebrate achievements and recognize hard work across the organization.
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="px-6 py-2.5 text-base font-semibold rounded-lg transition duration-300 shadow-xl flex items-center justify-center 
            bg-pink-600 text-white hover:bg-pink-700 focus:ring-4 focus:ring-pink-300 transform active:scale-95 w-full sm:w-auto"
        >
          <TrophyIcon className="h-5 w-5 mr-2" />
          Give Appreciation
        </button>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        <MetricCard 
          title="Total Shoutouts" 
          value={totalShoutouts} 
          icon={SparklesIcon}
          colorClass="text-pink-600" 
        />
        <MetricCard 
          title="Individual Awards" 
          value={individualShoutouts} 
          icon={UserIcon}
          colorClass="text-indigo-600" 
        />
        <MetricCard 
          title="Team Recognitions" 
          value={teamShoutouts} 
          icon={UsersIcon} 
          colorClass="text-teal-600" 
        />
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-8">
        
        {/* Search Input */}
        <div className="flex-grow flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 transition duration-150 shadow-sm">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by recipient, message, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 bg-transparent text-gray-700 font-roboto focus:outline-none"
          />
        </div>

        {/* Filter Dropdown */}
        <div className="md:w-56">
          <label className="text-xs font-poppins font-semibold text-gray-600 mb-1 block md:hidden">Filter by Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm font-roboto focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 shadow-sm"
          >
            <option value="All">All Recognitions</option>
            <option value="individual">Individual Awards</option>
            <option value="team">Team Recognitions</option>
          </select>
        </div>
      </div>

      {/* Appreciation Posts Grid */}
      <h3 className="text-xl font-poppins font-bold text-gray-800 mb-4 flex items-center">
        <HandThumbUpIcon className="h-6 w-6 mr-2 text-pink-600"/>
        Recent Shoutouts ({filteredPosts.length})
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPosts.length > 0 ? (
          filteredPosts.map(post => (
            <AppreciationCard key={post.id} post={post} />
          ))
        ) : (
          <div className="md:col-span-2 lg:col-span-3 p-8 text-center text-gray-500 bg-white rounded-xl shadow-inner border border-dashed border-gray-300">
            <p className="font-poppins text-lg">No shoutouts found.</p>
            <p className="text-sm mt-1">Be the first to recognize a colleague or team!</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component for KPI Cards
const MetricCard = ({ title, value, icon: Icon, colorClass }) => (
    <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col justify-between h-full transition duration-300 hover:shadow-lg hover:border-pink-500 transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-poppins font-bold text-gray-900 mt-2">{value}</p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass}/10`}>
                <Icon className={`h-7 w-7 ${colorClass}`} />
            </div>
        </div>
    </div>
);